import React from 'react';
import { Modal, Button, Link, FormPanel } from '@ixicare/ui';
import { Device, DeviceEntryProps, Profile, UserProfileEntryProps } from '@ixicare/resources';
import { useMutation, useQuery } from '@tanstack/react-query';
import { t } from '~/i18n';
import { useStores } from '~store/utils';
import styles from './DeviceModal.module.css';

interface DeviceLinkModalProps {
  device: DeviceEntryProps;
  newOwnerId: number;
  newOwnerName: string;
  onClose: () => void;
  onLinked: () => void;
}

export const DeviceLinkModal: React.FC<DeviceLinkModalProps> = ({
  device,
  newOwnerId,
  newOwnerName,
  onClose,
  onLinked,
}) => {
  const { notifications } = useStores();

  const { data: currentOwner } = useQuery<UserProfileEntryProps>({
    queryKey: ['profile', device.ownerId],
    queryFn: () => Profile.getProfile(device.ownerId!),
    enabled: !!device.ownerId,
  });

  const linkMutation = useMutation({
    mutationFn: () => Device.patchDevice(device.id, { ownerId: newOwnerId }),
    onSuccess: () => {
      notifications.addUserActionNotification({
        notificationType: 'success',
        message: t('scan.placed.resident.description', { name: newOwnerName }),
      });
      onLinked();
    },
    onError: (error) => {
      console.error('Error linking device:', error);
    },
  });

  const hasOwner = !!device.ownerId && !!currentOwner;

  return (
    <Modal onClose={onClose} style={{ maxWidth: 500 }}>
      <h3 className="text-center mb-30">{t('scan.residentSelect.title')}</h3>
      <FormPanel>
        <div className={styles.DeviceInfo}>
          {device.deviceType && (
            <img className={styles.DeviceImg} src={`/assets/${device.deviceType.toLowerCase()}@2x.png`} />
          )}
          <div className={styles.DeviceDetails}>
            <div className={styles.DeviceRow}>
              <span className={styles.DeviceLabel}>{t('devices.table.deviceType')}</span>
              <span>{device.deviceType ? t(`device.types.${device.deviceType.toLowerCase()}`) : ''}</span>
            </div>
            {device.macAddress && (
              <div className={styles.DeviceRow}>
                <span className={styles.DeviceLabel}>{t('devices.table.macAddress')}</span>
                <span>{device.macAddress}</span>
              </div>
            )}
            {device.imeiNumber && (
              <div className={styles.DeviceRow}>
                <span className={styles.DeviceLabel}>{t('devices.table.imeiNumber')}</span>
                <span>{device.imeiNumber}</span>
              </div>
            )}
          </div>
        </div>

        <div className={`${styles.DeviceRow} mb-15`}>
          <span className={styles.DeviceLabel}>{t('deviceLink.newAlarmSender')}</span>
          <Link href={`/residents/${newOwnerId}`}>{newOwnerName}</Link>
        </div>

        {hasOwner && (
          <div className={`${styles.DeviceRow} mb-15`}>
            <span className={styles.DeviceLabel}>{t('deviceLink.currentAlarmSender')}</span>
            <Link href={`/residents/${currentOwner.id}`}>
              {currentOwner.firstName} {currentOwner.lastName}
            </Link>
          </div>
        )}

        <div className="d-flex justify-content-end mt-30">
          <Button
            loading={linkMutation.isPending}
            disabled={linkMutation.isPending}
            onClick={() => linkMutation.mutate()}
          >
            {hasOwner ? t('deviceDetail.actions.swap') : t('deviceDetail.actions.link')}
          </Button>
        </div>
      </FormPanel>
    </Modal>
  );
};
